/**
 * DSA 2.0 — síntesis vascular temporal (MinIP / MIP) sobre frames sustraídos.
 */

import * as cornerstone from 'cornerstone-core'
import {
  dsaBaseImageId,
  dsaDefaultBias,
  ensureDsaOutBuffer,
  subtractFrame,
} from './dsaSubtract'

/**
 * @param {{
 *   baseImageId: string,
 *   fStart: number,
 *   fEnd: number,
 *   numberOfFrames: number,
 *   maskPixels: TypedArray,
 *   cols: number,
 *   rows: number,
 *   mode?: 'minip'|'mip',
 *   shiftX?: number,
 *   shiftY?: number,
 *   gain?: number,
 *   offset?: number,
 *   invert?: boolean,
 *   noiseFloor?: number,
 *   min?: number,
 *   max?: number,
 *   onProgress?: (k: number, total: number) => void,
 * }} opts
 * @returns {Promise<{ pixels: Float32Array, cols: number, rows: number, mode: string, frames: number }>}
 */
export async function computeTemporalSynth(opts) {
  const baseId = dsaBaseImageId(opts.baseImageId)
  if (!baseId) throw new Error('sin imageId')
  const nFrames = Math.max(1, opts.numberOfFrames | 0)
  let fStart = Math.max(0, Math.min(nFrames - 1, opts.fStart | 0))
  let fEnd = Math.max(0, Math.min(nFrames - 1, opts.fEnd | 0))
  if (fEnd < fStart) {
    const t = fStart
    fStart = fEnd
    fEnd = t
  }

  const cols = opts.cols | 0
  const rows = opts.rows | 0
  const n = cols * rows
  if (!opts.maskPixels || n <= 0) throw new Error('sin máscara')

  const mode = opts.mode === 'mip' ? 'mip' : 'minip'
  const acc = new Float32Array(n)
  const seed = mode === 'mip' ? -Infinity : Infinity
  for (let i = 0; i < n; i++) acc[i] = seed

  let outBuf = null
  const total = fEnd - fStart + 1
  let done = 0

  for (let f = fStart; f <= fEnd; f++) {
    const image = await cornerstone.loadAndCacheImage(`${baseId}?frame=${f}`)
    if (!image || typeof image.getPixelData !== 'function') {
      throw new Error(`frame ${f} sin píxeles`)
    }
    const live = image.getPixelData()
    const min =
      opts.min != null
        ? opts.min
        : image.minPixelValue != null
          ? Number(image.minPixelValue)
          : 0
    const max =
      opts.max != null
        ? opts.max
        : image.maxPixelValue != null
          ? Number(image.maxPixelValue)
          : 4095
    const offset =
      opts.offset != null && Number.isFinite(Number(opts.offset))
        ? Number(opts.offset)
        : dsaDefaultBias(image)

    outBuf = ensureDsaOutBuffer(live, outBuf)
    subtractFrame(live, opts.maskPixels, outBuf, {
      cols,
      rows,
      shiftX: opts.shiftX || 0,
      shiftY: opts.shiftY || 0,
      gain: opts.gain != null ? opts.gain : 1,
      offset,
      invert: !!opts.invert,
      noiseFloor: opts.noiseFloor != null ? opts.noiseFloor : 0,
      min,
      max,
    })

    if (mode === 'mip') {
      for (let i = 0; i < n; i++) {
        if (outBuf[i] > acc[i]) acc[i] = outBuf[i]
      }
    } else {
      for (let i = 0; i < n; i++) {
        if (outBuf[i] < acc[i]) acc[i] = outBuf[i]
      }
    }

    done += 1
    if (typeof opts.onProgress === 'function') {
      opts.onProgress(done, total)
    }
  }

  for (let i = 0; i < n; i++) {
    if (!Number.isFinite(acc[i])) acc[i] = 0
  }

  return { pixels: acc, cols, rows, mode, frames: total }
}

/**
 * Convierte píxeles float a PNG dataURL (VOI simple).
 * @param {Float32Array|TypedArray} pixels
 * @param {number} cols
 * @param {number} rows
 * @param {{ min?: number, max?: number }} [voi]
 */
export function synthPixelsToDataUrl(pixels, cols, rows, voi) {
  const canvas = document.createElement('canvas')
  canvas.width = cols
  canvas.height = rows
  const ctx = canvas.getContext('2d')
  const img = ctx.createImageData(cols, rows)
  const data = img.data
  let min = voi && voi.min != null ? voi.min : Infinity
  let max = voi && voi.max != null ? voi.max : -Infinity
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    min = Infinity
    max = -Infinity
    for (let i = 0; i < pixels.length; i++) {
      const v = pixels[i]
      if (v < min) min = v
      if (v > max) max = v
    }
    if (!(max > min)) {
      min = 0
      max = 1
    }
  }
  const span = max - min || 1
  for (let i = 0; i < cols * rows; i++) {
    const g = Math.max(0, Math.min(255, Math.round(((pixels[i] - min) / span) * 255)))
    const o = i * 4
    data[o] = g
    data[o + 1] = g
    data[o + 2] = g
    data[o + 3] = 255
  }
  ctx.putImageData(img, 0, 0)
  return canvas.toDataURL('image/png')
}

/**
 * Modo por defecto según invert (vasos oscuros → MinIP).
 * @param {boolean} invertDSA
 */
export function defaultSynthMode(invertDSA) {
  return invertDSA ? 'mip' : 'minip'
}
